import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { X, Save, Trash2 } from 'lucide-react';

export default function EditActivityForm({ activity, kidId, onClose }) {
  const { editActivity, deleteActivity, addActivity, currentUser } = useFamilyContext();
  const [formData, setFormData] = useState({
    name: activity.name,
    category: activity.category,
    energyLevel: activity.energyLevel,
    howToStart: activity.howToStart || '',
    description: activity.description || '',
    links: Array.isArray(activity.links) ? activity.links : []
  });
  
  const isParent = currentUser?.isParent;
  const isDuplicate = activity.isDuplicate; // This indicates we're creating a copy
  const isKidEditingOwn = !isParent && activity.kidId === currentUser?.id && activity.isCustom;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isDuplicate) {
      // Create a new activity for this kid
      const newActivity = {
        ...formData,
        isCustom: true,
        addedBy: 'user'
      };
      await addActivity(currentUser.id, newActivity);
    } else {
      // Edit existing activity
      editActivity(kidId, activity.id, formData);
    }
    
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${activity.name}"?`)) {
      deleteActivity(kidId, activity.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-stone-900">
            {isDuplicate ? 'Create My Copy' : isParent ? 'Edit Activity' : 'Edit My Activity'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Activity Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className={`w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                !isParent && !isDuplicate ? 'bg-stone-100 cursor-not-allowed' : ''
              }`}
              disabled={!isParent && !isDuplicate}
              required
            >
              <option value="physical">Physical</option>
              <option value="creative">Creative</option>
              <option value="learning">Learning</option>
              <option value="social">Social</option>
              <option value="quiet">Quiet Time</option>
              <option value="lifeSkills">Life Skills</option>
              <option value="family">Family</option>
              <option value="other">Other</option>
            </select>
            {!isParent && !isDuplicate && (
              <p className="text-xs text-stone-500 mt-1">Category cannot be changed for existing activities</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Energy Level
            </label>
            <select
              value={formData.energyLevel}
              onChange={(e) => setFormData(prev => ({ ...prev, energyLevel: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="low">Low Energy</option>
              <option value="medium">Medium Energy</option>
              <option value="high">High Energy</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="What is this activity about?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              How to Start (optional)
            </label>
            <textarea
              value={formData.howToStart}
              onChange={(e) => setFormData(prev => ({ ...prev, howToStart: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Quick tips for getting started..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Helpful Links (optional)
            </label>
            <textarea
              value={formData.links.join('\n')}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                links: e.target.value.split('\n').filter(link => link.trim()) 
              }))}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter URLs, one per line..."
            />
            <p className="text-xs text-stone-500 mt-1">Add helpful websites or resources (one per line)</p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-neutral-50 font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Save className="w-4 h-4 mr-2" />
              {isDuplicate ? 'Create My Copy' : 'Save Changes'}
            </button>
            {activity.isCustom && !isDuplicate && (isParent || isKidEditingOwn) && (
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-neutral-50 font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}